import { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { BookOpen, Play, ExternalLink, Star, Clock, Filter } from 'lucide-react';
import { motion } from 'framer-motion';
import { completeMission } from '../../utils/missionHelper';

// Learning resources curated for middle schoolers
const learningResources = [
  {
    id: 'ted-ed',
    title: 'TED-Ed',
    category: 'All Subjects',
    type: 'Videos',
    emoji: '🎬',
    color: 'from-red-500 to-pink-500',
    description: 'Amazing animated videos that explain everything!',
    duration: '5-10 min videos',
    link: 'https://ed.ted.com',
    topics: ['Science', 'History', 'Math', 'Art']
  },
  {
    id: 'crash-course',
    title: 'Crash Course Kids',
    category: 'Science',
    type: 'Videos',
    emoji: '🔬',
    color: 'from-green-500 to-teal-500',
    description: 'Fun science videos that make learning awesome!',
    duration: '5-12 min videos',
    link: 'https://www.youtube.com/crashcoursekids',
    topics: ['Earth Science', 'Life Science', 'Physical Science']
  },
  {
    id: 'duolingo',
    title: 'Duolingo',
    category: 'Languages',
    type: 'App/Game',
    emoji: '🦉',
    color: 'from-green-500 to-lime-500',
    description: 'Learn a new language by playing games!',
    duration: '5-15 min daily',
    link: 'https://www.duolingo.com',
    topics: ['Spanish', 'French', 'Japanese', '40+ languages']
  },
  {
    id: 'brilliant',
    title: 'Brilliant.org',
    category: 'Math & Science',
    type: 'Interactive',
    emoji: '💡',
    color: 'from-orange-500 to-amber-500',
    description: 'Interactive puzzles that make you think!',
    duration: '10-20 min lessons',
    link: 'https://brilliant.org',
    topics: ['Math', 'Logic', 'Science', 'Computer Science']
  },
  {
    id: 'typing',
    title: 'TypingClub',
    category: 'Skills',
    type: 'Practice',
    emoji: '⌨️',
    color: 'from-blue-500 to-indigo-500',
    description: 'Learn to type fast - a super useful skill!',
    duration: '10-15 min practice',
    link: 'https://www.typingclub.com',
    topics: ['Typing', 'Keyboard Skills']
  },
  {
    id: 'codecombat',
    title: 'CodeCombat',
    category: 'Coding',
    type: 'Game',
    emoji: '⚔️',
    color: 'from-purple-500 to-indigo-500',
    description: 'Learn to code by playing an adventure game!',
    duration: '15-30 min sessions',
    link: 'https://codecombat.com',
    topics: ['Python', 'JavaScript', 'Game Development']
  },
  {
    id: 'nat-geo-kids',
    title: 'National Geographic Kids',
    category: 'Science & Nature',
    type: 'Articles & Videos',
    emoji: '🌍',
    color: 'from-yellow-500 to-orange-500',
    description: 'Explore animals, nature, and the world!',
    duration: 'Browse anytime',
    link: 'https://kids.nationalgeographic.com',
    topics: ['Animals', 'Nature', 'Geography', 'History']
  },
  {
    id: 'prodigy',
    title: 'Prodigy Math',
    category: 'Math',
    type: 'Game',
    emoji: '🧙‍♂️',
    color: 'from-purple-500 to-pink-500',
    description: 'RPG game where you battle using math!',
    duration: '15-30 min sessions',
    link: 'https://www.prodigygame.com',
    topics: ['Math', 'Problem Solving']
  }
];

const categories = ['All', 'Coding', 'Math', 'Science', 'Languages', 'Skills'];

export default function MSLearnPage() {
  const { token } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const missionTriggered = useRef(false);

  const filteredResources = selectedCategory === 'All'
    ? learningResources
    : learningResources.filter(r => 
        r.category.toLowerCase().includes(selectedCategory.toLowerCase()) ||
        r.topics.some(t => t.toLowerCase().includes(selectedCategory.toLowerCase()))
      );

  return (
    <div className="p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Learning Portal</h1>
            <p className="text-slate-300">Fun ways to learn new things!</p>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm transition-all ${
                selectedCategory === cat
                  ? 'bg-cyan-500 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Resources Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {filteredResources.map((resource, index) => (
          <motion.div
            key={resource.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="glass-card p-6 hover:scale-[1.01] transition-transform"
          >
            <div className="flex items-start gap-4 mb-4">
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${resource.color} flex items-center justify-center text-2xl`}>
                {resource.emoji}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">{resource.title}</h3>
                <div className="flex flex-wrap gap-2 mt-1">
                  <span className="px-2 py-0.5 bg-slate-700 rounded-full text-xs text-slate-300">
                    {resource.category}
                  </span>
                  <span className="px-2 py-0.5 bg-slate-700 rounded-full text-xs text-slate-300">
                    {resource.type}
                  </span>
                </div>
              </div>
            </div>

            <p className="text-slate-300 text-sm mb-3">{resource.description}</p>

            <div className="flex items-center gap-2 text-slate-300 text-sm mb-4">
              <Clock className="w-4 h-4" />
              <span>{resource.duration}</span>
            </div>

            <div className="mb-4">
              <div className="flex flex-wrap gap-1">
                {resource.topics.map((topic, i) => (
                  <span key={i} className="px-2 py-1 bg-slate-700/50 rounded text-xs text-slate-300">
                    {topic}
                  </span>
                ))}
              </div>
            </div>

            <a
              href={resource.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (!missionTriggered.current && token) {
                  missionTriggered.current = true;
                  completeMission(token, 'learn');
                }
              }}
              className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-gradient-to-r ${resource.color} text-white font-medium hover:opacity-90 transition-opacity`}
            >
              <Play className="w-4 h-4" />
              Start Learning
            </a>
          </motion.div>
        ))}
      </div>

      {/* Tip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-8 glass-card p-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-cyan-500/30"
      >
        <p className="text-cyan-200 text-sm text-center">
          💡 <strong>Pro Tip:</strong> Spend just 15-20 minutes a day learning something new. Small steps lead to big results!
        </p>
      </motion.div>
    </div>
  );
}
